import type { Board } from "../board";
import type { DamageType } from "../damageType";
import { DestroyableObstacle } from "../destroyableObstacle";
import { ParticleEffect } from "../effects/particleEffect";
import type { PostDeserializable } from "../interfaces";
import { ParticleType } from "../particleType";
import { deserializeXY } from "../serialization";
import { STD_BASE_HP } from "../physicalThingWithHp";
import type { Thing } from "../thing";
import type { XY } from "../xy";
import { DestroyedConcreteBlock } from "./destroyedConcreteBlock";
import { Sound } from "./sounds/sound";
import { SoundId } from "./sounds/soundId";
import { WallCrack } from "./wallCrack";

export class ConcreteBlock extends DestroyableObstacle implements PostDeserializable {
	static key = "ConcreteBlock";
	private wallCrack: WallCrack | null = null;

	constructor(xy: XY) {
		super(xy);
		this.hp = STD_BASE_HP * 3;
		this.maxHp = STD_BASE_HP * 3;
	}

	getDebrisParticleType(): ParticleType {
		return ParticleType.CONCRETE_DEBRIS;
	}

	getDebrisCount(): number {
		return 10;
	}

	applyDamage(type: DamageType | null, amount: number, source: Thing | null): number {
		const dmg = super.applyDamage(type, amount, source);
		if (dmg > 0 && this.hp > 0 && this.board) {
			this.board.addEffect(new ParticleEffect(this.xy, ParticleType.CONCRETE_DEBRIS, 3));
		}
		this.updateCrack();
		return dmg;
	}

	private updateCrack(): void {
		const healthPercent = this.hp / this.maxHp;
		if (healthPercent >= 1) return;
		if (healthPercent <= 0.3) return;

		const targetStage = healthPercent > 0.6 ? 0 : 1;

		if (this.wallCrack) {
			this.wallCrack.setState(targetStage);
			return;
		}

		if (this.board) {
			this.wallCrack = new WallCrack(this.xy, targetStage);
			this.board.addThing(this.wallCrack);
		}
	}

	protected onZeroHp(source: Thing | null, type: DamageType | null): void {
		this.board!.addNonPhysicalThing(new Sound(SoundId.ROCK_BREAK, this.xy));
		if (this.wallCrack && this.board) {
			this.board.removeThing(this.wallCrack);
			this.wallCrack = null;
		}
		const remnant = new DestroyedConcreteBlock(this.xy);
		this.board!.addThing(remnant);
		super.onZeroHp(source, type);
	}

	postDeserialize(board: Board): void {
		this.wallCrack = board.getThingOfClass(WallCrack, this.xy);
	}

	static deserialize(data: any): ConcreteBlock {
		const c = new ConcreteBlock(deserializeXY(data.xy));
		c.restoreBaseFields(data);
		return c;
	}
}
